import type { ReactNode } from "react";

/**
 * Lightweight markdown renderer for AI-generated text.
 * Supports: **bold**, * bullet, - bullet, paragraphs, simple section labels.
 */
export function AiMarkdown({ text }: { text: string }) {
  if (!text) return null;

  const lines = text.split("\n").map((l) => l.trim());
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length) {
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="space-y-1.5 my-2">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
              <span className="text-primary mt-1">•</span>
              <span>{renderInline(b)}</span>
            </li>
          ))}
        </ul>,
      );
      bullets = [];
    }
  };

  for (const line of lines) {
    if (!line) {
      flushBullets();
      continue;
    }
    // Bullet (* or -)
    if (/^[*\-]\s+/.test(line)) {
      bullets.push(line.replace(/^[*\-]\s+/, ""));
      continue;
    }
    // Section heading (line that's just bold text or ends with :)
    const headingMatch = line.match(/^\*\*([^*]+):\*\*\s*(.*)$/);
    if (headingMatch) {
      flushBullets();
      const [, label, rest] = headingMatch;
      blocks.push(
        <div key={`h-${blocks.length}`} className="mt-3 first:mt-0">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          {rest && (
            <p className="text-sm leading-relaxed text-muted-foreground">{renderInline(rest)}</p>
          )}
        </div>,
      );
      continue;
    }
    // Plain bold-only heading like **Brief**
    const boldOnly = line.match(/^\*\*([^*]+)\*\*$/);
    if (boldOnly) {
      flushBullets();
      blocks.push(
        <p key={`h-${blocks.length}`} className="text-sm font-semibold text-foreground mt-3 first:mt-0">
          {boldOnly[1]}
        </p>,
      );
      continue;
    }
    // Regular paragraph
    flushBullets();
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-sm leading-relaxed text-muted-foreground">
        {renderInline(line)}
      </p>,
    );
  }
  flushBullets();

  return <div className="space-y-2">{blocks}</div>;
}

function renderInline(text: string): ReactNode {
  // Split by **bold** while preserving text
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const m = part.match(/^\*\*([^*]+)\*\*$/);
    if (m) {
      return (
        <strong key={i} className="text-foreground font-semibold">
          {m[1]}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
